import _Model from "./_Model";
import RelatedConnectionDevice from "./RelatedConnectionDevice";
import Validator from "./Validator";
import IOTypeWithNumber from './IOTypeWithNumber';
import Protocol from './Protocol';
import { OPTION_CATEGORIES, PAC_EXTENSION_MODULES, busExpansionType, candidateAccessoryType } from "../constants";
import SlottedAdditionalDevice from "./SlottedAdditionalDevice";
import DeviceAccessory from "./DeviceAccessory";
import CandidateAccessory from "./CandidateAccessory";
import BOMMap from "./BOMMap";

export default class Device extends _Model {
  constructor(json) {
    super(json);
    this.id = Device.getOrDefault(json, 'id', '');
    this.productType = Device.getOrDefault(json, 'productType', null);
    this.slot = Device.getMappedOrDefault(json, 'slot', 0, v => parseInt(v));
    this.isLastModule = Device.getOrDefault(json, 'isLastModule', false);
    this.slottedAdditionalDevices = Device.getMappedOrDefault(json, 'slottedAdditionalDevices', [], data => (data || []).map(p => new SlottedAdditionalDevice(p)));
    this.warranty = Device.getOrDefault(json, 'warranty', null);
    this.candidateAccessories = Device.getMappedOrDefault(json, 'candidateAccessories', [], data => (data || []).map(d => new CandidateAccessory(d)));
    this.accessories = Device.getMappedOrDefault(json, 'accessories', [], data => (data || []).map(d => new DeviceAccessory(d)));
    this.auxiliaries = Device.getMappedOrDefault(json, 'auxiliaries', [], data => (data || []).map(d => new DeviceAccessory(d)));
    this.sensors = Device.getMappedOrDefault(json, 'sensors', [], data => (data || []).map(d => new DeviceAccessory(d)));
    this.spareParts = Device.getMappedOrDefault(json, 'spareParts', [], data => (data || []).map(d => new DeviceAccessory(d)));
    this.options = Device.getMappedOrDefault(json, 'options', [], data => (data || []).map(d => new DeviceAccessory(d)));
    this.description = Device.getOrDefault(json, 'description', '');
    this.moduleType = Device.getOrDefault(json, 'moduleType', null);
    this.rangeOfProduct = Device.getOrDefault(json, 'rangeOfProduct', '');
    this.baseCartridgeURL = Device.getOrDefault(json, 'baseCartridgeURL', '');
    this.sidePicture = Device.getOrDefault(json, 'sidePicture', '');
    this.powerSupply = Device.getOrDefault(json, 'powerSupply', '');
    this.masterProtocols = Device.getMappedOrDefault(json, 'masterProtocols', [], data => (data || []).map(p => new Protocol(p)));
    this.slaveProtocols = Device.getMappedOrDefault(json, 'slaveProtocols', [], data => (data || []).map(p => new Protocol(p)));
    this.height = Device.getMappedOrDefault(json, 'height', 0.0, v => parseFloat(v));
    this.width = Device.getMappedOrDefault(json, 'width', 0.0, v => parseFloat(v));
    this.depth = Device.getMappedOrDefault(json, 'depth', 0.0, v => parseFloat(v));
    this.busExpansionType = Device.getMappedOrDefault(json, 'busExpansionType', busExpansionType.none, v => (v || busExpansionType.none).toLowerCase());
    this.controllerConsumption = Device.getMappedOrDefault(json, 'controllerConsumption', 0, v => parseInt(v));
    this.inputConsumption = Device.getMappedOrDefault(json, 'inputConsumption', 0, v => parseInt(v));
    this.consumption3Dot3V = Device.getMappedOrDefault(json, 'consumption3Dot3V', 0, v => parseInt(v));
    this.maxConsumption3Dot3V = Device.getMappedOrDefault(json, 'maxConsumption3Dot3V', 0, v => parseInt(v));
    this.consumption5V = Device.getMappedOrDefault(json, 'consumption5V', 0, v => parseFloat(v));
    this.maxConsumption5V = Device.getMappedOrDefault(json, 'maxConsumption5V', 0, v => parseInt(v));
    this.consumption24V = Device.getMappedOrDefault(json, 'consumption24V', 0, v => parseFloat(v));
    this.maxConsumption24V = Device.getMappedOrDefault(json, 'maxConsumption24V', 0, v => parseInt(v));
    this.consumption24VSensors = Device.getMappedOrDefault(json, 'consumption24VSensors', 0, v => parseInt(v));
    this.maxConsumption24VSensors = Device.getMappedOrDefault(json, 'maxConsumption24VSensors', 0, v => parseInt(v));
    this.hasCustomConsumptionLoads = Device.getMappedOrDefault(json, 'hasCustomConsumptionLoads', false, v => Boolean(v));
    this.consumptionLoads = Device.getMappedOrDefault(json, 'consumptionLoads', 0, v => parseFloat(v));
    this.maxConsumptionLoads = Device.getMappedOrDefault(json, 'maxConsumptionLoads', 0, v => parseInt(v));
    this.hasCustomConsumptionDevices = Device.getMappedOrDefault(json, 'hasCustomConsumptionDevices', false, v => Boolean(v));
    this.consumptionDevices = Device.getMappedOrDefault(json, 'consumptionDevices', 0, v => parseInt(v));
    this.maxConsumptionDevices = Device.getMappedOrDefault(json, 'maxConsumptionDevices', 0, v => parseInt(v));
    this.maxCartridges = Device.getMappedOrDefault(json, 'maxCartridges', 0, v => parseInt(v));
    this.maxExtensionRack = Device.getMappedOrDefault(json, 'maxExtensionRack', 0, v => parseInt(v));
    this.maxRemoteArea = Device.getMappedOrDefault(json, 'maxRemoteArea', 0, v => parseInt(v));
    this.electricalConnection = Device.getOrDefault(json, 'electricalConnection', '');
    this.cover = Device.getOrDefault(json, 'cover', '');
    this.relatedConnectionDevices = Device.getMappedOrDefault(json, 'relatedConnectionDevices', [], data => (data || []).map(d => new RelatedConnectionDevice(d || {})));
    this.hasFuse = Device.getMappedOrDefault(json, 'hasFuse', false, v => Boolean(v));
    this.canUsePowerSupply = Device.getMappedOrDefault(json, 'canUsePowerSupply', false, v => Boolean(v));
    this.relatedFuseDevices = Device.getMappedOrDefault(json, 'relatedFuseDevices', null, data => data || null);
    this.possibleSoftwareVersions = Device.getMappedOrDefault(json, 'possibleSoftwareVersions', [], data => (data || []).map(d => d));
    this.ioTypes = Device.getMappedOrDefault(json, 'ioTypes', [], data => (data || []).map(d => new IOTypeWithNumber(d || {})));
    this.additionalReferences = Device.getMappedOrDefault(json, 'additionalReferences', [], data => (data || []).map(d => d));

    this.maxNbIO = Device.getMappedOrDefault(json, 'maxNbIO', 0, v => parseInt(v));
    this.nbIOConsumed = Device.getMappedOrDefault(json, 'nbIOConsumed', 0, v => parseInt(v));
    this.commercializationStatus = Device.getOrDefault(json, 'commercializationStatus', 'N/A');
    this.validator = Device.getMappedOrDefault(json, 'validator', new Validator(), data => new Validator(data || {}));
  }

  processSlottedDevice(devicesToProcess) {
    const devices = [];
    for (const device of devicesToProcess) {

      for (let i = 0; i < device.quantity; i++) {
        devices.push(new Device({ id: device.id }));
      }
    }

    return devices;
  }

  get slottedDevices() {
    const sortedSlottedAdditionalDevices = this.slottedAdditionalDevices.sort((a, b) => a.layer - b.layer);
    const frontModules = [];
    const backModules = [];
    sortedSlottedAdditionalDevices.filter(sad => sad.layer < 0).forEach(sad => {
      if (sad.selectedDevice) {
        for (let i = 0; i < sad.selectedDevice.quantity; i++) {
          backModules.push(sad.selectedDevice.device);
        }
      }
    });
    sortedSlottedAdditionalDevices.filter(sad => sad.layer > 0).forEach(sad => {
      if (sad.selectedDevice) {
        for (let i = 0; i < sad.selectedDevice.quantity; i++) {
          frontModules.push(sad.selectedDevice.device);
        }
      }
    });
    return {
      'back': backModules,
      'front': frontModules,
    };
  }

  get slottedDevicesForBOM() {
    const sortedSlottedAdditionalDevices = this.slottedAdditionalDevices.sort((a, b) => a.layer - b.layer);
    return {
      'back': this.processSlottedDevice(sortedSlottedAdditionalDevices.filter(sad => sad.layer < 0).map(sad => sad.selectedDevice).filter(d => !!d)),
      'front': this.processSlottedDevice(sortedSlottedAdditionalDevices.filter(sad => sad.layer > 0).map(sad => sad.selectedDevice).filter(d => !!d)),
    };
  }

  getSlottedDevicesFrontImageLinks() {
    const sortedSlottedAdditionalDevices = this.slottedAdditionalDevices.sort((a, b) => a.layer - b.layer);
    const frontImages = [];
    sortedSlottedAdditionalDevices.filter(sad => sad.layer > 0).forEach(sad => {
      if (sad.selectedDevice) {
        frontImages.push({ src: sad.selectedDevice.imageLink, fallback: sad.selectedDevice.id, id: sad.selectedDevice.id });
      }
    });

    return frontImages;
  }

  setSlottedDeviceSelectedModule(layer, selectedModule) {
    const slottedDevice = this.slottedAdditionalDevices.find(sad => sad.layer === layer);
    slottedDevice.setSelectedDevice(selectedModule);
  }

  hasConsumption() {
    return this.consumption3Dot3V > 0 || this.consumption5V > 0 || this.consumption24V > 0 || this.consumption24VSensors > 0;
  }

  isPowerSupplier() {
    return this.maxConsumption3Dot3V > 0 || this.maxConsumption5V > 0 || this.maxConsumption24V > 0 || this.maxConsumption24VSensors > 0;
  }

  hasSpecialConsumptions() {
    return this.maxConsumptionLoads > 0 || this.maxConsumptionDevices > 0;
  }

  ioTypesAsObject() {
    const ioTypes = {};
    this.ioTypes.forEach((ioTypeWithNumber) => {
      if (ioTypes[ioTypeWithNumber.ioType.id]) {
        ioTypes[ioTypeWithNumber.ioType.id].number += ioTypeWithNumber.number;
      } else {
        ioTypes[ioTypeWithNumber.ioType.id] = Object.assign({}, ioTypeWithNumber);
      }
    });
    return ioTypes;
  }

  defaultSoftwareVersion() {
    const defaultSoftwareVersion = this.possibleSoftwareVersions.slice(-1)[0];
    return !!defaultSoftwareVersion ? defaultSoftwareVersion : null;
  }

  hasValidationError() {
    return !this.validator.valid;
  }

  extractReferences() {
    const bomMap = new BOMMap();
    bomMap.addReferencesToBOMMap(this.id, 1, this.description);
    if (this.accessories) {
      this.accessories.forEach(accessoryWithQuantity => {
        bomMap.addReferencesToBOMMap(accessoryWithQuantity.accessory, accessoryWithQuantity.quantity);
        if (accessoryWithQuantity.subAccessories) {
          accessoryWithQuantity.subAccessories.forEach(subAccessory => {
            bomMap.addReferencesToBOMMap(subAccessory.accessory, subAccessory.quantity);
          });
        }
      });
    }
    if (this.auxiliaries) {
      this.auxiliaries.forEach(accessoryWithQuantity => {
        bomMap.addReferencesToBOMMap(accessoryWithQuantity.accessory, accessoryWithQuantity.quantity);
        if (accessoryWithQuantity.subAccessories) {
          accessoryWithQuantity.subAccessories.forEach(subAccessory => {
            bomMap.addReferencesToBOMMap(subAccessory.accessory, subAccessory.quantity);
          });
        }
      });
    }
    if (this.sensors) {
      this.sensors.forEach(accessoryWithQuantity => {
        bomMap.addReferencesToBOMMap(accessoryWithQuantity.accessory, accessoryWithQuantity.quantity);
        if (accessoryWithQuantity.subAccessories) {
          accessoryWithQuantity.subAccessories.forEach(subAccessory => {
            bomMap.addReferencesToBOMMap(subAccessory.accessory, subAccessory.quantity);
          });
        }
      });
    }
    if (this.spareParts) {
      this.spareParts.forEach(accessoryWithQuantity => {
        bomMap.addReferencesToBOMMap(accessoryWithQuantity.accessory, accessoryWithQuantity.quantity);
        if (accessoryWithQuantity.subAccessories) {
          accessoryWithQuantity.subAccessories.forEach(subAccessory => {
            bomMap.addReferencesToBOMMap(subAccessory.accessory, subAccessory.quantity);
          });
        }
      });
    }
    if (this.options) {
      this.options.forEach(accessoryWithQuantity => {
        bomMap.addReferencesToBOMMap(accessoryWithQuantity.accessory, accessoryWithQuantity.quantity);

        if (accessoryWithQuantity.subAccessories) {
          accessoryWithQuantity.subAccessories.forEach(subAccessory => {
            bomMap.addReferencesToBOMMap(subAccessory.accessory, subAccessory.quantity);
          });
        }
      });
    }
    if (this.warranty) {
      bomMap.addReferencesToBOMMap(this.warranty, 1);
    }
    for (const additionalReference of [...this.additionalReferences]) {
      bomMap.addReferencesToBOMMap(additionalReference, 1);
    }

    return bomMap;
  }

  isM172RS485Module() {
    return this.id === 'TM171VEVM4';
  }

  allAccessories() {
    return [...this.accessories, ...this.auxiliaries, ...this.sensors, ...this.spareParts, ...this.options];
  }

  accessoriesId() {
    return this.allAccessories().map((acc) => acc.accessory);
  }

  recommandedAccessories() {
    const accessories = [];
    this.candidateAccessories.forEach((accessory) => {
      if (candidateAccessoryType.recommanded === accessory.type) {
        accessories.push(accessory.id);
      }
    });

    return accessories;
  }

  mandatoryAccessories() {
    const accessories = [];
    this.candidateAccessories.forEach((accessory) => {
      if (candidateAccessoryType.mandatory === accessory.type) {
        accessories.push(accessory.id);
      }
    });
    return accessories;
  }

  getPowerSupplyConsumption() {
    return this.consumption5V + this.consumption24V;
  }

  extractOptionsWithQuantity () {
    const result = {};
    const addToResult = (type, accessoryId, quantity) => {
      if (result[type] == null) {
        result[type] = {};
      }
      if (result[type][accessoryId] == null) {
        result[type][accessoryId] = 0;
      }
      result[type][accessoryId] += quantity;
    };

    this.accessories.forEach(a => {
      addToResult(OPTION_CATEGORIES.ACCESSORIES, a.accessory, a.quantity);
    });
    this.auxiliaries.forEach(a => {
      addToResult(OPTION_CATEGORIES.AUXILIARIES, a.accessory, a.quantity);
    });
    this.sensors.forEach(s => {
      addToResult(OPTION_CATEGORIES.SENSORS, s.accessory, s.quantity);
    });
    this.spareParts.forEach(s => {
      addToResult(OPTION_CATEGORIES.SPARE_PARTS, s.accessory, s.quantity);
    });
    this.options.forEach(o => {
      addToResult(OPTION_CATEGORIES.OPTIONS, o.accessory, o.quantity);
    });
    if (this.warranty) {
      addToResult(OPTION_CATEGORIES.WARRANTIES, this.warranty, 1);
    }
    return result;
  }

  extractSelectedOptions () {
    const result = {
      accessories: [],
      services: []
    };
    this.accessories.forEach(a => {
      result.accessories.push({
        id: a.accessory,
        quantity: a.quantity,
        minQty: a.minQty,
        maxQty: a.maxQty,
        accessoryType: OPTION_CATEGORIES.ACCESSORIES,
        subAccessories: a.extractSelectedSubAccessories()
      });
    });
    this.auxiliaries.forEach(a => {
      result.accessories.push({
        id: a.accessory,
        quantity: a.quantity,
        minQty: a.minQty,
        maxQty: a.maxQty,
        accessoryType: OPTION_CATEGORIES.AUXILIARIES,
        subAccessories: a.extractSelectedSubAccessories()
      });
    });
    this.sensors.forEach(s => {
      result.accessories.push({
        id: s.accessory,
        quantity: s.quantity,
        minQty: s.minQty,
        maxQty: s.maxQty,
        accessoryType: OPTION_CATEGORIES.SENSORS,
        subAccessories: s.extractSelectedSubAccessories()
      });
    });
    this.spareParts.forEach(s => {
      result.services.push({
        id: s.accessory,
        quantity: s.quantity,
        minQty: s.minQty,
        maxQty: s.maxQty,
        accessoryType: OPTION_CATEGORIES.SPARE_PARTS,
        subAccessories: s.extractSelectedSubAccessories()
      });
    });
    this.options.forEach(o => {
      result.accessories.push({
        id: o.accessory,
        quantity: o.quantity,
        minQty: o.minQty,
        maxQty: o.maxQty,
        accessoryType: OPTION_CATEGORIES.OPTIONS,
        subAccessories: o.extractSelectedSubAccessories()
      });
    });
    if (this.warranty) {
      result.services.push({
        id: this.warranty,
        quantity: 1,
        minQty: 0,
        maxQty: 1,
        accessoryType: OPTION_CATEGORIES.WARRANTIES,
        subAccessories: []
      });
    }

    return result;
  }

  extractSelectedSubAccessoryQuantityForAccessory = (accessoryId, subAccessoryId) => {
    let result = 0;
    const accessoriesAdded = [...this.accessories, ...this.auxiliaries, ...this.sensors, ...this.spareParts, ...this.options];
    accessoriesAdded.forEach(accessory => {
      if (accessory.accessory === accessoryId) {
        (accessory.subAccessories || []).forEach(subAccessory => {
          if (subAccessory.accessory === subAccessoryId) {
            result += subAccessory.quantity;
          }
        });
      }
    });
    return result;
  }

  isPacExtensionModule () {
    return this.id === PAC_EXTENSION_MODULES.BMXXBE1000 || this.id === PAC_EXTENSION_MODULES.BMXXBE1000H;
  }
}
